/**
 * The dashboard provider stack.
 *
 * Split in two on purpose:
 *  - {@link AppProviders} — always-on, session-independent layers: theme, the
 *    TanStack Query client, the session manager, the API client, wallet/web3,
 *    and the toast surface. Mounted once at the root.
 *  - {@link SessionScopedProviders} — the layers that need a LIVE session
 *    (feature flags, tenant context + branding, RBAC permissions). Mounted
 *    inside the authenticated shell, where `useSession()` is guaranteed.
 */
import * as React from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ApiClient } from "@authe/fe-commons/api";
import type { SessionManager } from "@authe/fe-commons/session";
import { FeatureFlags, type QaConfig } from "@authe/fe-commons/flags";
import { ThemeProvider } from "@authe/ui-core/theme";
import { SessionProvider, useSession } from "@authe/ui-core/session";
import { ApiProvider } from "@authe/ui-core/api";
import { FlagsProvider } from "@authe/ui-core/flags";
import { TenantProvider } from "@authe/ui-core/tenant";
import { PermissionProvider } from "@authe/ui-core/permissions";
import { Toaster } from "@authe/ui-core/providers";
import { TooltipProvider, notifySuccess } from "@authe/ui-core";
import { ObservabilityProvider } from "@authe/ui-core/observability";
import { Web3Provider, WalletProvider } from "@authe/web3";
import { StreamingProvider, type StreamingClient } from "@authe/streaming";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import type { AutheToken } from "@authe/fe-commons/api";
import { GraphqlProvider, preconnectGraphqlEndpoint } from "./graphql";
import { THEME_STORAGE_KEY, DEFAULT_BRAND, transportFlagDefaults, FLAG_OVERRIDE_STORAGE_KEY } from "@/config";
import { appEnv } from "@/config/env";
import { wagmiConfig } from "./web3";
import { setRoutingToken } from "./routing-token";
import { NameOverridesProvider, mergeTenancyNames, useNameOverrides } from "./tenancy-names";
import { DashboardPrefsProvider } from "./dashboardPrefs";

export interface AppProvidersProps {
  manager: SessionManager;
  apiClient: ApiClient;
  queryClient: QueryClient;
  /** Live-stream client; null when no stream endpoint is configured (Live mode off). */
  streamingClient?: StreamingClient | null;
  /** HTTP GraphQL read client; null when `PRIVATE_API_GRAPHQL_URL` is unset (REST only). */
  graphqlClient?: GraphQLClient | null;
  children: React.ReactNode;
}

/**
 * Mount `<StreamingProvider>` only when a client exists AND a live session is
 * established. Gating on auth is essential: `<StreamingProvider>` calls
 * `client.start()` on mount, which pulls a bearer (`getTokenSilently`) and opens
 * the socket. If that runs during the Auth0 redirect callback (this provider
 * sits above `<RequireSession>`), the streaming token fetch races the in-flight
 * `handleRedirectCallback` — the concurrent silent-auth/lock contention makes the
 * session's own `loadSession` token fetch fail ("loadSession failed despite
 * isAuthenticated"), leaving the app stuck on "Signing in", and the keepalive
 * ping fires unauthenticated (401). Waiting for `isAuthenticated` means the token
 * is cached and valid before the socket ever starts. Streaming hooks degrade to
 * a no-op (via `useOptionalStreamingClient`) until then.
 */
function MaybeStreaming({
  client,
  children,
}: {
  client: StreamingClient | null | undefined;
  children: React.ReactNode;
}): React.ReactElement {
  const { isAuthenticated } = useSession();
  if (!client || !isAuthenticated) return <>{children}</>;
  return <StreamingProvider client={client}>{children}</StreamingProvider>;
}

export function AppProviders({
  manager,
  apiClient,
  queryClient,
  streamingClient,
  graphqlClient,
  children,
}: AppProvidersProps): React.ReactElement {
  return (
    <ThemeProvider storageKey={THEME_STORAGE_KEY} defaultMode="system" defaultBrand={DEFAULT_BRAND}>
      <ObservabilityProvider env={appEnv} forwardLogs>
      <TooltipProvider delayDuration={200}>
        <QueryClientProvider client={queryClient}>
        <SessionProvider manager={manager}>
          <ApiProvider client={apiClient}>
           <GraphqlProvider client={graphqlClient ?? null}>
            {/*
              Wallet/web3 layer. `Web3Provider` supplies the wagmi (EVM) context
              and shares the app QueryClient; `WalletProvider` is the chain-
              agnostic hub (EVM + Solana + Bitcoin injected adapters) that powers
              `WalletMenu`. Both sit app-wide so the header chip and the Wallet
              page see the same connection state.
            */}
            <Web3Provider config={wagmiConfig} queryClient={queryClient}>
              {/*
                autoConnect is OFF on purpose. Its default (true) eagerly
                hydrates the injected EVM + Solana + Bitcoin adapters on mount,
                which makes multi-chain injected wallets (e.g. Phantom) pop their
                connect prompt on every page refresh. Wallets now connect only on
                explicit user action (the header chip / Wallet page). wagmi's own
                silent `reconnectOnMount` still restores an already-authorized EVM
                session without a prompt.
              */}
              <WalletProvider autoConnect={false}>
                <MaybeStreaming client={streamingClient}>
                  {children}
                  <Toaster />
                </MaybeStreaming>
              </WalletProvider>
            </Web3Provider>
           </GraphqlProvider>
          </ApiProvider>
        </SessionProvider>
        </QueryClientProvider>
      </TooltipProvider>
      </ObservabilityProvider>
    </ThemeProvider>
  );
}

/**
 * One-time "you're signed in" toast.
 *
 * The user's name + workspace live in the JWT, but nothing surfaces them after a
 * login — and a tenant switch mints a NEW token (a fresh login event) behind a
 * full reload, so the user can lose track of which workspace they landed in.
 * This pops a friendly confirmation once per (user, tenant) per browser tab:
 * `sessionStorage` survives the tenant-switch reload, so switching tenants keys
 * a new entry and re-announces, while ordinary navigation/token-refresh stays
 * quiet. High-severity live events still toast via the notification centre.
 */
function SessionWelcomeToast(): null {
  const { session } = useSession();

  React.useEffect(() => {
    if (!session) return;
    const userId = session.user.id ?? "anon";
    const tenantId = session.tenant.tenantId ?? "none";
    const key = `dashboard.welcome.${userId}.${tenantId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // Private mode / storage disabled — skip the dedupe, but avoid a loop by
      // only firing on this mount.
    }

    const name = session.names.userName ?? session.user.name ?? session.user.email ?? "there";
    // Prefer the resolved friendly names, falling back to the raw ids.
    const scope = [
      session.names.orgName ?? session.tenant.orgId,
      session.names.tenantName ?? session.tenant.tenantId,
    ]
      .filter((p): p is string => Boolean(p))
      .join(" · ");
    notifySuccess(`Signed in as ${name}`, {
      description: scope ? `Workspace ${scope}` : undefined,
    });
  }, [session]);

  return null;
}

/**
 * Flags + tenant + permissions sourced from the live session's claims. Render
 * only where a session exists (inside `<RequireSession>`); falls back to safe
 * empties otherwise so it never throws while the guard redirects.
 *
 * Wraps the session-scoped layers in `<NameOverridesProvider>` so a rename
 * handler anywhere below can optimistically echo a new friendly name (which
 * `<SessionScopedLayers>` overlays onto the token-resolved names before they
 * reach `<TenantProvider>`).
 */
export function SessionScopedProviders({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <NameOverridesProvider>
      <SessionScopedLayers>{children}</SessionScopedLayers>
    </NameOverridesProvider>
  );
}

function SessionScopedLayers({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session } = useSession();
  const nameOverrides = useNameOverrides();

  // Feed the ApiClient's region router the live decoded token. Done in render
  // (not an effect) on purpose: this component is an ancestor of every page, so
  // its render runs before any child's fetch effect — guaranteeing the token is
  // present the first time a request resolves its region/host. The token merges
  // the namespaced id-token claims (carrying `region_config` / `continent`) with
  // the raw access token for the `Authorization` bearer.
  React.useMemo(() => {
    if (!session) {
      setRoutingToken(null);
      return;
    }
    const idClaims = session.idClaims as Record<string, unknown>;
    setRoutingToken({
      ...idClaims,
      __raw: session.accessToken ?? "",
      sub: typeof idClaims.sub === "string" ? idClaims.sub : "",
    } satisfies AutheToken);
    // Token snapshot is now live, so the GraphQL endpoint resolves to the
    // caller's REGIONAL AppSync custom domain. Warm DNS+TLS to it now (before
    // any page's first read effect fires) so the first composed read is fast.
    preconnectGraphqlEndpoint();
  }, [session]);

  // Transport defaults (which API each operation uses) come from the central
  // `config/transport` switchboard; Settings › APIs writes per-user overrides
  // into the SAME override keyspace, and precedence is `override > claim >
  // default` — so a tenant claim or a user override still wins.
  const flags = React.useMemo(
    () =>
      new FeatureFlags({
        defaults: transportFlagDefaults(),
        claims: session?.claims.feature_flags ?? {},
        qaConfig: (session?.claims.qa_config as QaConfig | undefined) ?? null,
        overrideStorageKey: FLAG_OVERRIDE_STORAGE_KEY,
      }),
    [session],
  );

  // Overlay any optimistic rename echoes onto the token-resolved names so a
  // just-saved customer/org/tenant name shows immediately, before the token
  // round-trip catches up.
  const names = React.useMemo(
    () => (session ? mergeTenancyNames(session.names, session.tenant, nameOverrides) : null),
    [session, nameOverrides],
  );

  // No session yet → deny-all permissions + a global/empty tenant. The route
  // guard is already redirecting, so this content won't actually paint.
  if (!session) {
    return (
      <FlagsProvider flags={flags}>
        <TenantProvider tenant={{ orgId: null, tenantId: null }}>
          <PermissionProvider rolePermissions={{}} isLoading>
            <DashboardPrefsProvider>{children}</DashboardPrefsProvider>
          </PermissionProvider>
        </TenantProvider>
      </FlagsProvider>
    );
  }

  return (
    <FlagsProvider flags={flags}>
      <TenantProvider tenant={session.tenant} names={names ?? session.names}>
        <PermissionProvider rolePermissions={session.claims.role_permissions ?? {}}>
          <SessionWelcomeToast />
          <DashboardPrefsProvider>{children}</DashboardPrefsProvider>
        </PermissionProvider>
      </TenantProvider>
    </FlagsProvider>
  );
}
